<script>
import {
  CdrText,
  CdrButton,
  CdrImg,
  IconXLg,
  IconInformationFill,
  CdrLink,
  CdrInput,
  CdrSelect,
  IconErrorStroke,
} from '@rei/cedar';
import AlertComponent from '../common/alert/AlertComponent.vue';
import CONSTANTS from '../../utils/constants';
import { returnDayOption, returnYearOption, monthOption } from '../../utils/scheduleData';
import specialGiftCard from '../../utils/specialGiftcard';
import formMessageError from '../../utils/formMessageError';
import analytics from '../../common/analytics';

const initialState = () => ({
  analytics,
  showCaption: false,
  messageCaption: '',
  labelAmount: CONSTANTS.DEFAULT_AMOUNT_LABEL,
  rangeAmount: CONSTANTS.DEFAULT_AMOUNT_RANGE_LABEL,
  disabledAmount: false,
  maxAmount: 1000,
  minAmount: 10,
  toInput: '',
  fromInput: '',
  amountInput: null,
  recepientEmailInput: '',
  confirmEmailInput: '',
  dayInput: `${new Date().getDate().toString() < 10 ? '0' : ''}${new Date().getDate().toString()}`,
  monthInput: `${(new Date().getMonth() + 1).toString() < 10 ? '0' : ''}${(new Date().getMonth() + 1).toString()}`,
  yearInput: new Date().getFullYear().toString(),
  deliveryDateInput: '',
  messageInput: '',
  dayOption: returnDayOption(new Date().getFullYear(), new Date().getMonth() + 1),
  monthOption,
  yearOption: returnYearOption(),
  counterMessageCharacter: CONSTANTS.INITIAL_MESSAGE_CHARACTER,
  emailRegex: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
  analyticsErrorMsg: [],
  isAlert: false,
  alertType: CONSTANTS.TYPE_ERROR_SERVICE_ADD_CART,
  textAlert: CONSTANTS.MSG_ERROR_SERVICE_ADD_CART,
  errorInputs: {
    toError: false,
    fromError: false,
    amountError: false,
    emailError: false,
    confirmEmailError: false,
    deliveryError: false,
  },
  errorMessage: {
    toError: formMessageError.blurValidation.toError,
    fromError: formMessageError.blurValidation.fromError,
    amountError: formMessageError.blurValidation.amountError,
    emailError: formMessageError.blurValidation.emailError,
    confirmEmailError: formMessageError.blurValidation.emailConfirmError,
    deliveryError: formMessageError.formValidation.deliveryError,
  },
});

export default {
  name: 'GiftcardFormInputs',
  components: {
    AlertComponent,
    CdrText,
    CdrButton,
    CdrImg,
    IconXLg,
    IconInformationFill,
    CdrLink,
    CdrInput,
    CdrSelect,
    IconErrorStroke,
  },
  props: {
    showForm: {
      type: Boolean,
      required: true,
    },
    imgSelected: {
      type: Object,
      required: true,
    },
    cmsContent: {
      type: Object,
      required: true,
    },
    deliveryMethod: {
      type: String,
      required: true,
    },
    errors: {
      type: Array,
      required: true,
    },
    openAlert: {
      type: Boolean,
      required: true,
    },
    siteId: {
      type: String,
      required: true,
    },
    pageDescription: {
      type: String,
      required: true,
    },
    productStyles: {
      type: String,
      required: true,
    },
    preFillValue: {
      type: String,
      default: '0.00',
    },
  },
  emits: [
    'onCloseForm',
    'onUpdateIsOpenAlert',
    'onPreview',
    'onPurchaseAnalytic',
    'onPurchaseForm',
  ],
  data() {
    return initialState();
  },
  watch: {
    imgSelected(giftCard) {
      this.checkSpecialGiftCard(giftCard.mediaCode);
    },
    deliveryMethod(newValue, oldValue) {
      if (newValue === oldValue) return;
      this.deliveryDateInput = newValue === 'EMAIL' ? `${this.monthInput}/${this.dayInput}/${this.yearInput}` : '';
      this.resetValidations();
    },
    messageInput(newValue) {
      this.counterMessageCharacter = newValue.length === 0 ? CONSTANTS.INITIAL_MESSAGE_CHARACTER : `You have ${120 - newValue.length} characters remaining.`;
      const strClean = this.removeSpecialCharacter(newValue);
      this.messageInput = strClean;
    },
    toInput(newValue) {
      this.errorInputs.toError = false;
      const strClean = this.removeSpecialCharacter(newValue);
      this.toInput = strClean;
      this.inputToValidation();
    },
    fromInput(newValue) {
      this.errorInputs.fromError = false;
      const strClean = this.removeSpecialCharacter(newValue);
      this.fromInput = strClean;
      this.inputFromValidation();
    },
    amountInput(newValue) {
      if (!newValue) return;
      this.errorInputs.amountError = false;
      const amount = newValue.toString();
      this.amountInput = (amount.indexOf('.') >= 0) ? (amount.substr(0, amount.indexOf('.')) + amount.substr(amount.indexOf('.'), 3)) : amount;
      this.inputAmountValidation();
    },
    recepientEmailInput() {
      this.errorInputs.emailError = false;
      this.inputConfirmEmailValidation(this.confirmEmailInput, false);
    },
    confirmEmailInput(newValue) {
      this.inputConfirmEmailValidation(newValue);
    },
    dayInput(newDay) {
      this.deliveryDateInput = `${this.monthInput}/${newDay}/${this.yearInput}`;
      this.inputDateValidation();
    },
    monthInput(newMonth) {
      this.dayOption = returnDayOption(this.yearInput, newMonth);
      this.checkNewDay(this.dayOption);
      this.deliveryDateInput = `${newMonth}/${this.dayInput}/${this.yearInput}`;
      this.inputDateValidation();
    },
    yearInput(newYear) {
      this.dayOption = returnDayOption(newYear, this.monthInput);
      this.checkNewDay(this.dayOption);
      this.deliveryDateInput = `${this.monthInput}/${this.dayInput}/${newYear}`;
      this.inputDateValidation();
    },
    errors(errors) {
      if (errors.length > 0) this.setErrors(errors);
    },
    openAlert(isOpen) {
      this.isAlert = isOpen;
    },
  },
  mounted() {
    this.amountInput = this.preFillValue === '0.00' ? null : this.preFillValue;
    this.checkSpecialGiftCard(this.imgSelected.mediaCode);
  },
  methods: {
    reset() {
      Object.assign(this.$data, initialState());
    },
    onClose() {
      this.reset();
      this.removeSpecialCard();
      this.$emit('onCloseForm', false);
    },
    checkSpecialGiftCard(mediaCode) {
      switch (mediaCode) {
        case specialGiftCard.E99999:
          this.setSpecialCard(
            this.cmsContent.hipcampDescription,
            CONSTANTS.HIPCAMP_VALUE,
            CONSTANTS.SPECIAL_AMOUNT_LABEL,
            true,
            CONSTANTS.HIPCAMP_RANGE_LABEL
          );
          break;

        case specialGiftCard.E62764:
        case specialGiftCard.G62764:
          this.setSpecialCard(
            this.cmsContent.membershipDescription,
            this.preFillValue === '0.00' ? CONSTANTS.MEMBERSHIP_VALUE : this.preFillValue,
            CONSTANTS.SPECIAL_AMOUNT_LABEL,
            false,
            CONSTANTS.MEMBERSHIP_RANGE_VALUE,
            CONSTANTS.MEMBERSHIP_MIN_VALUE,
            CONSTANTS.MEMBERSHIP_MAX_VALUE
          );
          break;

        case specialGiftCard.G62683:
          this.setSpecialCard(
            CONSTANTS.BRAILE_MSG,
            this.preFillValue === '0.00' ? null : this.preFillValue,
            CONSTANTS.SPECIAL_AMOUNT_LABEL,
            false,
            CONSTANTS.DEFAULT_AMOUNT_RANGE_LABEL,
            CONSTANTS.BRAILE_MIN_VALUE,
            CONSTANTS.BRAILE_MAX_VALUE
          );
          break;

        default:
          this.removeSpecialCard();
          break;
      }
    },
    setSpecialCard(
      message = '',
      valueAmount = 0,
      inputLabel = 'Gift amount',
      disabledInput = false,
      amountInputMsg = '($10 - $1,000)',
      minValue = 10,
      maxValue = 1000
    ) {
      this.showCaption = true;
      this.messageCaption = message;
      this.amountInput = valueAmount;
      this.labelAmount = inputLabel;
      this.disabledAmount = disabledInput;
      this.rangeAmount = amountInputMsg;
      this.maxAmount = maxValue;
      this.minAmount = minValue;
    },
    removeSpecialCard() {
      this.showCaption = false;
      this.messageCaption = '';
      this.amountInput = this.amountInput ?? null;
      this.labelAmount = 'Enter an amount';
      this.disabledAmount = false;
      this.rangeAmount = '($10 - $1,000)*';
      this.maxAmount = 1000;
      this.minAmount = 10;
    },
    removeSpecialCharacter(str) {
      const regex = /[^ña-zÑA-ZÀ-ÿ0-9\u00E0-\u00FC :;,!@#?`%^*)(+=._-]/gm;
      return str.replace(regex, '');
    },
    blurValidationInput({ target: { name } }) {
      switch (name) {
        case 'to':
          this.inputToValidation();
          break;

        case 'from':
          this.inputFromValidation();
          break;

        case 'amount':
          this.inputAmountValidation();
          break;

        case 'recipientEmail':
          this.inputEmailValidation();
          break;

        case 'recipientConfirmationEmail':
          this.inputConfirmEmailValidation(this.confirmEmailInput);
          break;

        default:
          break;
      }
    },
    setErrors(errors) {
      errors.forEach((key) => {
        switch (key) {
          case 'TO_EMPTY':
          case 'TO_INVALID':
            this.errorInputs.toError = true;
            this.errorMessage.toError = (key === 'TO_EMPTY') ? formMessageError.formValidation.toError : formMessageError.formValidation.toErrorMax;
            break;

          case 'FROM_EMPTY':
          case 'FROM_INVALID':
            this.errorInputs.fromError = true;
            this.errorInputs.fromError = (key === 'FROM_EMPTY') ? formMessageError.formValidation.fromError : formMessageError.formValidation.fromErrorMax;
            break;

          case 'AMOUNT_INVALID':
            this.errorInputs.amountError = true;
            this.errorMessage.amountError = formMessageError.formValidation.amountError;
            break;

          case 'RECIPIENT_EMAIL_EMPTY':
          case 'RECIPIENT_EMAIL_INVALID':
            this.errorInputs.emailError = true;
            this.errorMessage.emailError = (key === 'RECIPIENT_EMAIL_EMPTY') ? formMessageError.blurValidation.emailError : formMessageError.formValidation.emailError;
            break;

          case 'RECIPIENT_CONFIRMATION_EMAIL_INVALID':
            this.errorInputs.confirmEmailError = true;
            this.errorMessage.confirmEmailError = formMessageError.formValidation.emailConfirmError;
            break;

          default:
            break;
        }
      });
    },
    isFormValid() {
      this.resetValidations();

      this.inputToValidation(true);
      this.inputFromValidation(true);
      this.inputAmountValidation(this.amountInput, true);
      this.inputEmailValidation(true);
      this.inputConfirmEmailValidation(this.confirmEmailInput, true, true);
      this.inputDateValidation(true);

      return !this.errorInputs.fromError
        && !this.errorInputs.toError
        && !this.errorInputs.amountError
        && !this.errorInputs.emailError
        && !this.errorInputs.confirmEmailError
        && !this.errorInputs.deliveryError;
    },
    sendAnalyticClickEvent(eventName) {
      this.analytics.click({ linkName: `${this.$props.siteId}:${this.$props.pageDescription}_${eventName}` });
    },
    onCloseAlert(value) {
      this.isAlert = value;
      this.$emit('onUpdateIsOpenAlert', value);
    },
    checkNewDay(daysOfMonth = []) {
      const lastDayAvaliable = daysOfMonth.length;
      if (this.dayInput > lastDayAvaliable) {
        this.dayInput = lastDayAvaliable;
      }
    },
    resetValidations() {
      this.errorInputs = {
        toError: false,
        fromError: false,
        amountError: false,
        emailError: false,
        confirmEmailError: false,
        deliveryError: false,
      };
    },
    onOpenPreview(e) {
      e.preventDefault();
      if (!this.isFormValid()) {
        window.scrollTo({ top: this.$refs.toInputRef.$el.offsetTop - 20 });
        return;
      }
      this.sendAnalyticClickEvent('preview gift card');
      this.$emit('onPreview', {
        to: this.toInput,
        from: this.fromInput,
        amount: parseFloat(this.amountInput).toFixed(2),
        recepientEmail: this.recepientEmailInput,
        confirmEmail: this.confirmEmailInput,
        day: this.dayInput,
        month: this.monthInput,
        year: this.yearInput,
        message: this.messageInput.trim(),
        giftCard: this.imgSelected,
        deliveryDate: `${this.monthInput}/${this.dayInput}/${this.yearInput}`,
      });
    },
    async onPurchaseForm() {
      if (!this.isFormValid()) {
        const data = {
          error: true,
          analyticsErrorMsg: this.analyticsErrorMsg,
          giftCardSelected: this.imgSelected,
          deliveryDate: `${this.monthInput}/${this.dayInput}/${this.yearInput}`,
        };
        this.$emit('onPurchaseAnalytic', data);
        window.scrollTo({ top: this.$refs.toInputRef.$el.offsetTop - 20 });
        return;
      }
      const giftCardItem = {
        deliveryMethod: this.deliveryMethod,
        mediaCode: this.imgSelected.mediaCode,
        to: this.toInput,
        from: this.fromInput,
        amount: parseFloat(this.amountInput).toFixed(2),
        message: this.messageInput.trim(),
        ...(this.deliveryMethod === 'EMAIL' && {
          recipientEmail: this.recepientEmailInput,
          recipientConfirmationEmail: this.confirmEmailInput,
          deliveryDate: `${this.monthInput}/${this.dayInput}/${this.yearInput}`,
        }),
      };
      this.$emit('onPurchaseForm', giftCardItem);
    },
    inputToValidation(pushAnalyticError = false) {
      this.errorInputs.toError = (this.toInput === '' || this.toInput.length > 35);
      this.errorMessage.toError = this.toInput === '' ? formMessageError.blurValidation.toError : formMessageError.blurValidation.toErrorMax;
      if ((this.toInput === '' || this.toInput.length > 35) && pushAnalyticError) {
        this.analyticsErrorMsg.push(this.toInput === '' ? formMessageError.formValidation.toError : formMessageError.formValidation.toErrorMax);
      }
    },
    inputFromValidation(pushAnalyticError = false) {
      this.errorInputs.fromError = (this.fromInput === '' || this.fromInput.length > 35);
      this.errorMessage.fromError = this.fromInput === '' ? formMessageError.blurValidation.fromError : formMessageError.blurValidation.fromErrorMax;
      if ((this.fromInput === '' || this.fromInput.length > 35) && pushAnalyticError) {
        this.analyticsErrorMsg.push(this.fromInput === '' ? formMessageError.formValidation.fromError : formMessageError.formValidation.fromErrorMax);
      }
    },
    inputAmountValidation(amountValue = this.amountInput, pushAnalyticError = false) {
      const amountValueFloat = parseFloat(amountValue || 0);
      this.errorInputs.amountError = ((amountValueFloat < this.minAmount || amountValueFloat > this.maxAmount) && this.imgSelected.mediaCode !== 'E99999');
      this.errorMessage.amountError = formMessageError.blurValidation.amountError;
      if (this.imgSelected.mediaCode === 'E99999' && amountValueFloat !== 100) {
        this.errorInputs.amountError = true;
        this.errorMessage.amountError = formMessageError.formValidation.amountError;
      }
      if (
        (((amountValueFloat < this.minAmount || amountValueFloat > this.maxAmount) && this.imgSelected.id !== 'E99999')
        || (this.imgSelected.mediaCode === 'E99999' && amountValueFloat !== 100)) && pushAnalyticError) {
        this.analyticsErrorMsg.push(formMessageError.formValidation.amountError);
      }
    },
    inputEmailValidation(pushAnalyticError = false) {
      this.errorInputs.emailError = (this.recepientEmailInput === '' || !this.recepientEmailInput.match(this.emailRegex)) && this.deliveryMethod === 'EMAIL';
      this.errorMessage.emailError = this.recepientEmailInput === ''
        ? formMessageError.blurValidation.emailError
        : formMessageError.formValidation.emailError;
      if ((this.recepientEmailInput === '' || !this.recepientEmailInput.match(this.emailRegex)) && this.deliveryMethod === 'EMAIL' && pushAnalyticError) {
        this.analyticsErrorMsg.push(formMessageError.formValidation.emailError);
      }
    },
    inputConfirmEmailValidation(confirmEmail, checkEmpty = true, pushAnalyticError = false) {
      if (confirmEmail === '' && this.deliveryMethod === 'EMAIL') {
        this.errorInputs.confirmEmailError = !!checkEmpty;
        this.errorMessage.confirmEmailError = formMessageError.blurValidation.emailConfirmError;
      } else if (!confirmEmail.match(this.emailRegex) && this.deliveryMethod === 'EMAIL') {
        this.errorInputs.confirmEmailError = true;
        this.errorMessage.confirmEmailError = formMessageError.formValidation.emailConfirmError;
        if (pushAnalyticError) {
          this.analyticsErrorMsg.push(formMessageError.formValidation.emailConfirmError);
        }
      } else if (confirmEmail !== this.recepientEmailInput && this.deliveryMethod === 'EMAIL') {
        this.errorInputs.confirmEmailError = true;
        this.errorMessage.confirmEmailError = formMessageError.formValidation.matchError;
        if (pushAnalyticError) {
          this.analyticsErrorMsg.push(formMessageError.formValidation.matchError);
        }
      } else {
        this.errorInputs.confirmEmailError = false;
      }
    },
    inputDateValidation(pushAnalyticError = false) {
      this.errorInputs.deliveryError = false;
      const todayDate = new Date();
      const nextYear = new Date(
        new Date().setFullYear(new Date().getFullYear() + 1)
      );
      const monthMMM = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const deliveryDate = new Date(
        parseInt(this.yearInput, 10),
        parseInt(this.monthInput, 10) - 1,
        parseInt(this.dayInput, 10)
      ).setHours(0, 0, 0, 0);
      if (deliveryDate < todayDate.setHours(0, 0, 0, 0) && this.deliveryMethod === 'EMAIL') {
        this.errorInputs.deliveryError = true;
        this.errorMessage.deliveryError = `
          ${formMessageError.formValidation.deliveryErrorOldDate} Minimum Date ${monthMMM[todayDate.getMonth()]}/${todayDate.getDate()}/${todayDate.getFullYear()}.
        `;
        if (pushAnalyticError) {
          this.analyticsErrorMsg.push(formMessageError.formValidation.deliveryErrorOldDate);
        }
      }
      if (deliveryDate > nextYear.setHours(0, 0, 0, 0) && this.deliveryMethod === 'EMAIL') {
        this.errorInputs.deliveryError = true;
        this.errorMessage
          .deliveryError = `
            ${formMessageError.formValidation.deliveryError} Maximum Date ${monthMMM[nextYear.getMonth()]}/${nextYear.getDate()}/${nextYear.getFullYear()}.
          `;
        if (pushAnalyticError) {
          this.analyticsErrorMsg.push(formMessageError.formValidation.deliveryError);
        }
      }
    },
    onFocusAmountInput() {
      this.$nextTick(() => {
        this.$refs.amountInputReference
          .$el
          .children[0]
          .children[0]
          .focus();
      });
    },
  },
};
</script>

<template>
  <form
    v-if="showForm"
    autocomplete="off"
    data-ui="gift-card-form-input-container"
  >
    <div class="giftcard-form-input__content">
      <section class="giftcard-form-input__header">
        <cdr-text class="giftcard-form-input__title">
          Card Details
        </cdr-text>
        <cdr-button
          class="giftcard-form-input__btn-close"
          modifier="secondary"
          :icon-only="true"
          aria-label="close form icon"
          @click="onClose"
        >
          <template #icon>
            <icon-x-lg
              class="giftcard-form-input__icon-close"
              inherit-color
            />
          </template>
        </cdr-button>
      </section>

      <section class="giftcard-form-input__body">
        <cdr-img
          :src="imgSelected.mediaPath"
          :alt="imgSelected.description"
          :data-media-code="`${imgSelected.mediaCode}`"
          class="giftcard-form-input__img-selected"
          data-ui="gift-card-selected-card"
          fetchprioirty="high"
        />
        <div class="giftcard-form-input__information-container">
          <cdr-text
            v-if="imgSelected.category"
            class="giftcard-form-input__information-category"
          >
            {{ imgSelected.category }}
          </cdr-text>
          <cdr-text
            v-if="imgSelected.description"
            class="giftcard-form-input__information-description"
            data-ui="gift-card-information-description"
          >
            {{ imgSelected.description }}
          </cdr-text>
        </div>
        <div
          v-if="showCaption"
          id="caption-container"
          data-ui="gift-card-caption-container"
          class="giftcard-form-input__caption"
        >
          <icon-information-fill class="giftcard-form-input__caption-icon" />
          <p
            class="giftcard-form-input__caption-description"
            v-html="messageCaption"
          />
        </div>
        <cdr-input
          ref="toInputRef"
          v-model="toInput"
          name="to"
          label-class="giftcard-form-input__input"
          label="To"
          type="text"
          placeholder="Recipient's name"
          :error="errorInputs.toError"
          required
          autocomplete=""
          data-ui="gift-card-recipient-name"
          @blur="blurValidationInput"
        >
          <template #error>
            <span data-ui="gift-card-recipient-name-error-message">
              {{ errorMessage.toError }}
            </span>
          </template>
        </cdr-input>
        <cdr-input
          v-model="fromInput"
          name="from"
          label-class="giftcard-form-input__input"
          label="From"
          type="text"
          placeholder="Sender's name"
          :error="errorInputs.fromError"
          required
          autocomplete=""
          data-ui="gift-card-sender-name"
          @blur="blurValidationInput"
        >
          <template #error>
            <span data-ui="gift-card-sender-name-error-message">
              {{ errorMessage.fromError }}
            </span>
          </template>
        </cdr-input>
        <div style="width: 100%">
          <cdr-text
            class="giftcard-form-input__input-label"
            @click="onFocusAmountInput"
          >
            {{ `${labelAmount}` }}
          </cdr-text>
          <cdr-text
            class="giftcard-form-input__input-option"
            @click="onFocusAmountInput"
          >
            {{ `${rangeAmount}` }}
          </cdr-text>
          <cdr-input
            ref="amountInputReference"
            v-model="amountInput"
            name="amount"
            label-class="giftcard-form-input__input-amount"
            label="Enter an amount"
            type="number"
            placeholder="0.00"
            autocomplete=""
            :hide-label="true"
            :max="maxAmount"
            :min="minAmount"
            :disabled="disabledAmount"
            required
            :error="errorInputs.amountError"
            data-ui="gift-card-amount"
            @blur="blurValidationInput"
          >
            <template #pre-icon>
              <cdr-text class="giftcard-form-input__input-icon">
                $
              </cdr-text>
            </template>
            <template #error>
              <span data-ui="gift-card-amount-error-message">
                {{ errorMessage.amountError }}
              </span>
            </template>
          </cdr-input>
        </div>
        <cdr-input
          v-if="deliveryMethod === 'EMAIL'"
          v-model="recepientEmailInput"
          name="recipientEmail"
          label-class="giftcard-form-input__input"
          label="Recipient's email address"
          type="email"
          placeholder="Enter email"
          autocomplete=""
          required
          data-ui="gift-card-recipient-email"
          :error="errorInputs.emailError"
          @blur="blurValidationInput"
        >
          <template #error>
            <span data-ui="gift-card-recipient-email-error-message">
              {{ errorMessage.emailError }}
            </span>
          </template>
        </cdr-input>
        <cdr-input
          v-if="deliveryMethod === 'EMAIL'"
          v-model="confirmEmailInput"
          name="recipientConfirmationEmail"
          label-class="giftcard-form-input__input"
          label="Confirm recipient's email address"
          type="email"
          placeholder="Enter email"
          autocomplete=""
          required
          data-ui="gift-card-recipient-confirm-email"
          :error="errorInputs.confirmEmailError"
          @blur="blurValidationInput"
        >
          <template #error>
            <span data-ui="gift-card-recipient-confirm-email-error-message">
              {{ errorMessage.confirmEmailError }}
            </span>
          </template>
        </cdr-input>
        <cdr-text
          v-if="deliveryMethod === 'EMAIL'"
          class="giftcard-form-input__schedule-label"
        >
          Schedule a delivery date *
        </cdr-text>
        <cdr-text
          v-if="deliveryMethod === 'EMAIL'"
          class="giftcard-form-input__schedule-description"
        >
          You may schedule a delivery date up to year out
        </cdr-text>
        <div
          v-if="deliveryMethod === 'EMAIL'"
          class="giftcard-form-input__schedule-container"
        >
          <cdr-select
            v-model="monthInput"
            label=""
            data-ui="gift-card-delivery-date-month"
            :hide-label="true"
            :options="monthOption"
          />
          <cdr-select
            v-model="dayInput"
            label=""
            data-ui="gift-card-delivery-date-day"
            :hide-label="true"
            :options="dayOption"
          />
          <cdr-select
            v-model="yearInput"
            label=""
            data-ui="gift-card-delivery-date-year"
            :hide-label="true"
            :options="yearOption"
          />
        </div>
        <cdr-text
          v-if="errorInputs.deliveryError"
          class="giftcard-form-input__error-message"
        >
          <icon-error-stroke class="giftcard-form-input__error-icon" />
          {{ errorMessage.deliveryError }}
        </cdr-text>
        <cdr-input
          v-model="messageInput"
          label-class="giftcard-form-input__input"
          label="Message"
          name="message"
          type="text"
          :rows="4"
          maxlength="120"
          placeholder="Happy Birthday! I thought you might like..."
          autocomplete=""
          data-ui="gift-card-message"
          spellcheck="true"
          optional
        >
          <template #helper-text-top>
            This message field does not support emojis or some special characters
          </template>
          <template #helper-text-bottom>
            {{ counterMessageCharacter }}
          </template>
        </cdr-input>
        <div class="giftcard-form__cta">
          <cdr-button
            v-if="deliveryMethod === 'EMAIL'"
            class="giftcard-form-input__preview"
            modifier="secondary"
            data-ui="gift-card-preview-button"
            :full-width="true"
            @click="onOpenPreview"
          >
            Preview your card order
          </cdr-button>
          <cdr-button
            class="giftcard-form-input__addcart"
            data-ui="gift-card-add-to-cart-button"
            :full-width="true"
            @click="onPurchaseForm"
          >
            Add to cart
          </cdr-button>
          <div
            v-if="isAlert"
            class="giftcard-form-input__alert"
            data-ui="giftcard-form-message-alert"
          >
            <alert-component
              :opened="isAlert"
              :alert-type="alertType"
              :text="textAlert"
              @onCloseAlert="onCloseAlert"
            />
          </div>
        </div>
        <cdr-text class="giftcard-form-input__termsconditions">
          Read the gift card
          <cdr-link
            modifier="standalone"
            href="/terms/gift-card"
            data-ui="gift-card-terms-and-conditions"
            @click="sendAnalyticClickEvent('terms and conditions')"
          >
            terms and conditions
          </cdr-link>
        </cdr-text>
      </section>
    </div>
  </form>
</template>
